/**
 * Parcels & Neighbors Screen - MOBILE ONLY
 * 
 * Shows parcel details and L1/L2/L3 neighboring properties.
 * Neighbors powered by pre-computed adjacency graph for instant results.
 * Integrates with NC OneMap for parcel data.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, ScrollView, Pressable, StyleSheet, ActivityIndicator, Linking, Modal, TextInput, Alert, KeyboardAvoidingView, Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, space, text, icon, radius } from '../../../shared/tokens/mobile';
import { ScreenContainer, ScreenHeader } from '../../../components/v2/mobile';
import { ParcelCard, Parcel, NeighborTier } from '../../../components/v2/parcels';
import { authApi } from '../../../shared/utils/api';
import { logger } from '../../../shared/utils/logger';
import { formatCurrency, getCountyRecordUrls } from '../../../shared/utils/formatters';
import { NeighborsByTier } from '../../../shared/types/parcel';

export default function ParcelsScreen() {
  const router = useRouter();
  const { portfolioId, opportunityId, opportunityName } = useLocalSearchParams<{
    portfolioId: string;
    opportunityId: string;
    opportunityName?: string;
  }>();

  const handleBack = useCallback(() => router.back(), [router]);

  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [neighborTiers, setNeighborTiers] = useState<NeighborsByTier[]>([]);
  const [neighborSource, setNeighborSource] = useState<'graph' | 'api_cache' | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null);
  const [expandedTier, setExpandedTier] = useState<number | null>(1);
  const [linking, setLinking] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingParcel, setAddingParcel] = useState(false);
  const [managingMode, setManagingMode] = useState(false);

  // Fetch parcels and neighbors
  const fetchParcels = useCallback(async () => {
    if (!portfolioId || !opportunityId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authApi.getPropertyFeatures(parseInt(opportunityId, 10), {
        include_neighbors: true,
        max_tier: 3,
      });
      // Always update state from response to avoid stale data
      const mappedParcels: Parcel[] = (response.features ?? [])
        .filter((f: any) => 
          f.layer_code === 'parcel' && 
          f.association_type === 'primary'
        )
        .map((f: any) => {
          const parseNum = (val: any): number | undefined => {
            if (val == null) return undefined;
            const num = typeof val === 'string' ? parseFloat(val) : val;
            return isNaN(num) ? undefined : num;
          };
          
          return {
            id: f.id || f.feature_id,
            parcel_id: f.parcel_id || f.external_id || 'Unknown',
            owner_name: f.owner_name,
            address: f.site_address || f.address,
            acreage: parseNum(f.area_acres) ?? parseNum(f.acreage) ?? parseNum(f.calculated_acres),
            land_value: parseNum(f.land_value),
            building_value: parseNum(f.building_value),
            total_value: parseNum(f.total_value) ?? ((parseNum(f.land_value) || 0) + (parseNum(f.building_value) || 0)),
            land_use: f.land_use_description || f.land_use || f.land_class,
            zoning: f.zoning_code || f.zoning_description || f.zoning,
            deed_book: f.deed_book,
            deed_page: f.deed_page,
            sale_date: f.last_sale_date || f.sale_date,
            sale_price: parseNum(f.last_sale_price) ?? parseNum(f.sale_price),
            county_name: f.county_name,
          };
        });
      setParcels(mappedParcels);
      
      // Reset selection if current selection is no longer in the list
      setSelectedParcel(prev => {
        if (mappedParcels.length === 0) return null;
        if (prev && mappedParcels.some(p => p.id === prev.id)) return prev;
        return mappedParcels[0];
      });
      
      logger.info('Parcels loaded', 'ParcelsScreen', {
        primaryParcels: mappedParcels.length,
        totalFeatures: (response.features ?? []).length,
      });
      
      // Always set neighbor data (clears stale state when no neighbors returned)
      setNeighborTiers(response.neighborsByTier ?? []);
      setNeighborSource(response.neighborSource ?? null);
      if (response.neighborsByTier && response.neighborsByTier.length > 0) {
        logger.info('Neighbors loaded', 'ParcelsScreen', { 
          tiers: response.neighborsByTier.length,
          source: response.neighborSource,
          totalNeighbors: response.neighborsByTier.reduce((sum: number, t: NeighborsByTier) => sum + t.total_count, 0),
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load parcels';
      logger.error('Failed to fetch parcels', err, 'ParcelsScreen');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, opportunityId]);

  useEffect(() => {
    fetchParcels();
  }, [fetchParcels]);

  const handleOpenGIS = useCallback((parcelId: string, countyName?: string) => {
    const urls = getCountyRecordUrls(countyName, parcelId);
    const url = urls.gisUrl || urls.ncOneMapUrl;
    Linking.openURL(url);
  }, []);

  const handleLinkParcel = useCallback(async () => {
    if (!opportunityId) return;
    setLinking(true);
    try {
      const response = await authApi.linkPropertyToParcel(parseInt(opportunityId, 10), {
        auto_link: true,
        include_neighbors: false,
      });
      if (response.success) {
        logger.info('Parcel linked successfully', 'ParcelsScreen');
        await fetchParcels();
      } else {
        setError(response.message || 'Failed to link parcel');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to link parcel';
      logger.error('Failed to link parcel', err, 'ParcelsScreen');
      setError(message);
    } finally {
      setLinking(false);
    }
  }, [opportunityId, fetchParcels]);

  const handleSearchParcel = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchResults([]);
    try {
      const response = await authApi.lookupParcel({
        parcel_id: searchQuery.trim(),
        include_geometry: false,
      });
      if (response.feature) {
        setSearchResults([response.feature]);
      } else if ((response as any).features) {
        setSearchResults((response as any).features);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      logger.error('Parcel search failed', err, 'ParcelsScreen');
      Alert.alert('Search Failed', 'Could not find parcels. Try a different ID.');
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  const handleAddParcel = useCallback(async (featureId: number) => {
    if (!opportunityId) return;
    setAddingParcel(true);
    try {
      const response = await authApi.linkPropertyToParcel(parseInt(opportunityId, 10), {
        feature_id: featureId,
        auto_link: false,
        association_type: 'primary',
      });
      if (response.success) {
        Alert.alert('Success', 'Parcel added to opportunity');
        setShowAddModal(false);
        setSearchQuery('');
        setSearchResults([]);
        await fetchParcels();
      } else {
        Alert.alert('Error', response.message || 'Failed to add parcel');
      }
    } catch (err) {
      Alert.alert('Error', 'Failed to add parcel');
    } finally {
      setAddingParcel(false);
    }
  }, [opportunityId, fetchParcels]);

  const handleRemoveParcel = useCallback(async (parcel: Parcel) => {
    if (!opportunityId) return;
    Alert.alert(
      'Remove Parcel',
      `Remove parcel ${parcel.parcel_id} from this opportunity?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              const response = await authApi.unlinkParcelFromOpportunity(
                parseInt(opportunityId, 10),
                parcel.id
              );
              if (response.success) {
                await fetchParcels();
              } else {
                Alert.alert('Error', response.message || 'Failed to remove parcel');
              }
            } catch (err) {
              Alert.alert('Error', 'Failed to remove parcel');
            }
          },
        },
      ]
    );
  }, [opportunityId, fetchParcels]);

  if (loading) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Parcels & Neighbors" onBack={handleBack} />
        <View style={styles.center}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      </ScreenContainer>
    );
  }

  if (error) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Parcels & Neighbors" onBack={handleBack} />
        <View style={styles.center}>
          <Ionicons name="alert-circle-outline" size={icon.xxl} color={color.error} />
          <Text style={styles.errorText}>{error}</Text>
          <Pressable onPress={fetchParcels} style={styles.retryButton}>
            <Text style={styles.retryButtonText}>Try Again</Text>
          </Pressable>
        </View>
      </ScreenContainer>
    );
  }

  if (parcels.length === 0) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Parcels & Neighbors" onBack={handleBack} />
        <View style={styles.center}>
          <Ionicons name="map-outline" size={icon.xxl} color={color.textMuted} />
          <Text style={styles.emptyTitle}>No Parcels Linked</Text>
          <Text style={styles.emptySubtitle}>
            Link parcel data to see boundaries, neighbors, and details
          </Text>
          <Pressable 
            style={[styles.linkButton, linking && styles.linkButtonDisabled]} 
            onPress={handleLinkParcel}
            disabled={linking}
          >
            {linking ? (
              <ActivityIndicator size="small" color={color.surface} />
            ) : (
              <>
                <Ionicons name="link" size={icon.sm} color={color.surface} />
                <Text style={styles.linkButtonText}>Link Parcel Data</Text>
              </>
            )}
          </Pressable>
        </View>
      </ScreenContainer>
    );
  }

  const totalAcreage = parcels.reduce((sum, p) => {
    const acres = typeof p.acreage === 'number' ? p.acreage : parseFloat(String(p.acreage || 0)) || 0;
    return sum + acres;
  }, 0);
  const totalValue = parcels.reduce((sum, p) => {
    const value = typeof p.total_value === 'number' ? p.total_value : parseFloat(String(p.total_value || 0)) || 0;
    return sum + value;
  }, 0);

  return (
    <ScreenContainer>
      <ScreenHeader title={opportunityName || 'Parcels & Neighbors'} onBack={handleBack} />

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        {/* Map Actions */}
        <View style={styles.mapPlaceholder}>
          <Ionicons name="map" size={icon.xxl} color={color.textMuted} />
          <Text style={styles.mapPlaceholderText}>View parcels on map with L1/L2/L3 neighbors</Text>
          <View style={styles.mapButtonRow}>
            <Pressable 
              style={styles.mapButton} 
              onPress={() => router.push({
                pathname: '/v2/map',
                params: { 
                  focusOpportunityId: opportunityId,
                  showParcels: 'true'
                }
              })}
            >
              <Ionicons name="map-outline" size={icon.sm} color={color.primary} />
              <Text style={styles.mapButtonText}>View on Map</Text>
            </Pressable>
            {selectedParcel && (
              <Pressable 
                style={[styles.mapButton, styles.mapButtonSecondary]} 
                onPress={() => handleOpenGIS(selectedParcel.parcel_id, selectedParcel.county_name)}
              >
                <Ionicons name="open-outline" size={icon.sm} color={color.textMuted} />
                <Text style={[styles.mapButtonText, styles.mapButtonTextSecondary]}>County GIS</Text>
              </Pressable>
            )}
          </View>
        </View>

        {/* Summary */}
        <View style={styles.summaryCard}>
          <Text style={styles.sectionTitle}>Summary</Text>
          <View style={styles.summaryRow}>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>{parcels.length}</Text>
              <Text style={styles.summaryLabel}>Parcels</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>
                {typeof totalAcreage === 'number' ? totalAcreage.toFixed(2) : '0.00'}
              </Text>
              <Text style={styles.summaryLabel}>Total Acres</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>{formatCurrency(totalValue)}</Text>
              <Text style={styles.summaryLabel}>Tax Value</Text>
            </View>
          </View>
        </View>

        {/* Parcel List */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Parcels ({parcels.length})</Text>
            <View style={styles.sectionActions}>
              {managingMode && (
                <Pressable style={styles.addParcelButton} onPress={() => setShowAddModal(true)}>
                  <Ionicons name="add-circle" size={icon.sm} color={color.primary} />
                  <Text style={styles.addParcelText}>Add</Text>
                </Pressable>
              )}
              <Pressable
                style={[styles.manageButton, managingMode && styles.manageButtonActive]}
                onPress={() => setManagingMode(!managingMode)}
              >
                <Ionicons name={managingMode ? 'checkmark' : 'build-outline'} size={14} color={managingMode ? color.surface : color.primary} />
                <Text style={[styles.manageButtonText, managingMode && styles.manageButtonTextActive]}>
                  {managingMode ? 'Done' : 'Manage'}
                </Text>
              </Pressable>
            </View>
          </View>
          {parcels.map((parcel, index) => (
            <View key={parcel.id} style={styles.parcelRow}>
              <View style={styles.parcelCardWrap}>
                <ParcelCard
                  parcel={parcel}
                  index={index}
                  isSelected={selectedParcel?.id === parcel.id}
                  onPress={() => setSelectedParcel(parcel)}
                  onOverride={(field, value) => {
                    setParcels(prev => prev.map(p =>
                      p.id === parcel.id ? { ...p, [field]: value } : p
                    ));
                    if (selectedParcel?.id === parcel.id) {
                      setSelectedParcel(prev => prev ? { ...prev, [field]: value } : prev);
                    }
                  }}
                />
              </View>
              {managingMode && (
                <Pressable
                  style={styles.removeParcelButton}
                  onPress={() => handleRemoveParcel(parcel)}
                  accessibilityLabel={`Remove parcel ${parcel.parcel_id}`}
                  accessibilityRole="button"
                >
                  <Ionicons name="remove-circle" size={icon.lg} color={color.error} />
                </Pressable>
              )}
            </View>
          ))}
        </View>

        {/* Neighbors Section */}
        {neighborTiers.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Adjacent Properties</Text>
            {neighborTiers.map((tier) => (
              <NeighborTier
                key={tier.tier}
                tier={tier}
                isExpanded={expandedTier === tier.tier}
                neighborSource={neighborSource}
                onToggle={() => setExpandedTier(expandedTier === tier.tier ? null : tier.tier)}
                onNeighborPress={handleOpenGIS}
              />
            ))}
          </View>
        )}

        {/* No Neighbors State */}
        {neighborTiers.length === 0 && parcels.length > 0 && (
          <View style={styles.noNeighborsCard}>
            <Ionicons name="git-network-outline" size={icon.lg} color={color.textMuted} />
            <Text style={styles.noNeighborsTitle}>No neighbor data yet</Text>
            <Text style={styles.noNeighborsSubtitle}>
              {(() => {
                const county = selectedParcel?.county_name || parcels[0]?.county_name;
                if (county) {
                  return `Neighbor data is computed for ${county} County parcels`;
                }
                return 'Neighbor data is computed for supported counties';
              })()}
            </Text>
          </View>
        )}

        <View style={{ height: space.xxl }} />
      </ScrollView>

      {/* Add Parcel Search Modal */}
      <Modal visible={showAddModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAddModal(false)}>
        <KeyboardAvoidingView style={styles.modalContainer} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Add Parcel</Text>
            <Pressable onPress={() => { setShowAddModal(false); setSearchResults([]); setSearchQuery(''); }}>
              <Ionicons name="close" size={icon.lg} color={color.textPrimary} />
            </Pressable>
          </View>

          <Text style={styles.modalSubtitle}>Search by parcel ID to add to this opportunity</Text>

          <View style={styles.searchRow}>
            <TextInput
              style={styles.searchInput}
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="Enter parcel ID (e.g. 1234-56-7890)"
              placeholderTextColor={color.textMuted}
              returnKeyType="search"
              onSubmitEditing={handleSearchParcel}
              autoFocus
            />
            <Pressable style={styles.searchButton} onPress={handleSearchParcel} disabled={searching || !searchQuery.trim()}>
              {searching ? (
                <ActivityIndicator size="small" color={color.surface} />
              ) : (
                <Ionicons name="search" size={icon.sm} color={color.surface} />
              )}
            </Pressable>
          </View>

          <ScrollView style={styles.searchResults}>
            {searchResults.length === 0 && !searching && searchQuery.length > 0 && (
              <Text style={styles.noResultsText}>No parcels found. Try a different ID.</Text>
            )}
            {searchResults.map((feature: any) => (
              <View key={feature.id} style={styles.searchResultCard}>
                <View style={styles.searchResultInfo}>
                  <Text style={styles.searchResultId}>{feature.parcel_id || feature.external_id || 'Unknown'}</Text>
                  <Text style={styles.searchResultOwner} numberOfLines={1}>{feature.owner_name || 'Unknown Owner'}</Text>
                  {feature.site_address && <Text style={styles.searchResultAddress} numberOfLines={1}>{feature.site_address}</Text>}
                  <Text style={styles.searchResultMeta}>
                    {feature.area_acres ? `${parseFloat(feature.area_acres).toFixed(2)} acres` : ''}
                    {feature.county_name ? ` • ${feature.county_name} County` : ''}
                  </Text>
                </View>
                <Pressable
                  style={[styles.addResultButton, addingParcel && styles.addResultButtonDisabled]}
                  onPress={() => handleAddParcel(feature.id)}
                  disabled={addingParcel}
                >
                  {addingParcel ? (
                    <ActivityIndicator size="small" color={color.surface} />
                  ) : (
                    <>
                      <Ionicons name="add" size={14} color={color.surface} />
                      <Text style={styles.addResultButtonText}>Add</Text>
                    </>
                  )}
                </Pressable>
              </View>
            ))}
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { padding: space.md },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: space.xl },
  errorText: { fontSize: text.body.fontSize, color: color.error, marginTop: space.md, textAlign: 'center' },
  retryButton: { marginTop: space.lg, paddingHorizontal: space.lg, paddingVertical: space.sm, backgroundColor: color.primary, borderRadius: radius.md },
  retryButtonText: { color: color.surface, fontWeight: text.bodyMedium.fontWeight },
  emptyTitle: { fontSize: text.cardTitle.fontSize, fontWeight: text.cardTitle.fontWeight, color: color.textPrimary, marginTop: space.md },
  emptySubtitle: { fontSize: text.body.fontSize, color: color.textMuted, marginTop: space.xs, textAlign: 'center', paddingHorizontal: space.lg },
  linkButton: { flexDirection: 'row', alignItems: 'center', gap: space.sm, marginTop: space.lg, paddingHorizontal: space.xl, paddingVertical: space.md, backgroundColor: color.primary, borderRadius: radius.md },
  linkButtonDisabled: { opacity: 0.6 },
  linkButtonText: { color: color.surface, fontWeight: text.bodyMedium.fontWeight, fontSize: text.body.fontSize },
  mapPlaceholder: { height: 200, backgroundColor: color.backgroundSecondary, borderRadius: radius.lg, alignItems: 'center', justifyContent: 'center', marginBottom: space.md },
  mapPlaceholderText: { fontSize: text.body.fontSize, color: color.textMuted, marginTop: space.sm },
  mapButtonRow: { flexDirection: 'row', gap: space.sm, marginTop: space.md, justifyContent: 'center' },
  mapButton: { flexDirection: 'row', alignItems: 'center', gap: space.xs, paddingHorizontal: space.md, paddingVertical: space.sm, backgroundColor: color.surface, borderRadius: radius.md },
  mapButtonSecondary: { backgroundColor: color.backgroundSecondary },
  mapButtonText: { fontSize: text.caption.fontSize, color: color.primary, fontWeight: text.bodyMedium.fontWeight },
  mapButtonTextSecondary: { color: color.textMuted },
  summaryCard: { backgroundColor: color.surface, borderRadius: radius.lg, padding: space.md, marginBottom: space.md, borderWidth: 1, borderColor: color.divider },
  sectionTitle: { fontSize: text.bodyMedium.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, marginBottom: space.sm },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-around' },
  summaryItem: { alignItems: 'center' },
  summaryValue: { fontSize: text.sectionTitle.fontSize, fontWeight: text.sectionTitle.fontWeight, color: color.primary },
  summaryLabel: { fontSize: text.caption.fontSize, color: color.textMuted, marginTop: 2 },
  section: { marginBottom: space.lg },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.sm },
  sectionActions: { flexDirection: 'row', gap: space.xs },
  addParcelButton: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: space.sm, paddingVertical: space.xs, backgroundColor: color.primary + '15', borderRadius: radius.md },
  addParcelText: { fontSize: text.caption.fontSize, fontWeight: '600', color: color.primary },
  manageButton: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: space.sm, paddingVertical: space.xs, backgroundColor: color.backgroundSecondary, borderRadius: radius.md, borderWidth: 1, borderColor: color.border },
  manageButtonActive: { backgroundColor: color.primary, borderColor: color.primary },
  manageButtonText: { fontSize: text.caption.fontSize, fontWeight: '500', color: color.primary },
  manageButtonTextActive: { color: color.surface },
  parcelRow: { flexDirection: 'row', alignItems: 'center' },
  parcelCardWrap: { flex: 1 },
  removeParcelButton: { padding: space.xs, marginLeft: space.xs },
  modalContainer: { flex: 1, backgroundColor: color.background, padding: space.md },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: space.md },
  modalTitle: { fontSize: text.sectionTitle.fontSize, fontWeight: text.sectionTitle.fontWeight, color: color.textPrimary },
  modalSubtitle: { fontSize: text.body.fontSize, color: color.textMuted, marginBottom: space.md },
  searchRow: { flexDirection: 'row', gap: space.sm, marginBottom: space.md },
  searchInput: { flex: 1, backgroundColor: color.surface, borderRadius: radius.md, paddingHorizontal: space.md, paddingVertical: space.sm, fontSize: text.body.fontSize, color: color.textPrimary, borderWidth: 1, borderColor: color.border },
  searchButton: { backgroundColor: color.primary, borderRadius: radius.md, paddingHorizontal: space.md, alignItems: 'center', justifyContent: 'center' },
  searchResults: { flex: 1 },
  noResultsText: { textAlign: 'center', color: color.textMuted, fontSize: text.body.fontSize, paddingVertical: space.xl },
  searchResultCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: color.surface, borderRadius: radius.md, padding: space.md, marginBottom: space.sm, borderWidth: 1, borderColor: color.divider },
  searchResultInfo: { flex: 1 },
  searchResultId: { fontSize: text.bodyMedium.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.primary },
  searchResultOwner: { fontSize: text.body.fontSize, color: color.textPrimary, marginTop: 2 },
  searchResultAddress: { fontSize: text.caption.fontSize, color: color.textMuted, marginTop: 1 },
  searchResultMeta: { fontSize: text.caption.fontSize, color: color.textMuted, marginTop: 2 },
  addResultButton: { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: color.primary, borderRadius: radius.md, paddingHorizontal: space.md, paddingVertical: space.sm },
  addResultButtonDisabled: { opacity: 0.6 },
  addResultButtonText: { fontSize: text.caption.fontSize, fontWeight: '600', color: color.surface },
  noNeighborsCard: { backgroundColor: color.surface, borderRadius: radius.md, padding: space.lg, alignItems: 'center', borderWidth: 1, borderColor: color.divider },
  noNeighborsTitle: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, marginTop: space.sm },
  noNeighborsSubtitle: { fontSize: text.caption.fontSize, color: color.textMuted, marginTop: space.xs, textAlign: 'center' },
});
